import { CompactText } from 'ui';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft } from 'lucide-react';

import { formatBulletinSubjectTitle } from 'utils/bulletinLabels';

import classes from '../Chat.module.scss';
import screen from '../ChatScreen.module.scss';
import type { ChatRole, ContactShareState, RelatedBulletin, User } from '../types';
import { formatDisplayName } from '../utils/formatUserName';
import { formatLastSeen } from '../utils/formatLastSeen';
import { useGlobalPresence } from '../store/GlobalPresenceContext';
import { ChatAvatar } from './ChatAvatar';
import { ChatReportModal } from './ChatReportModal';
import {
  activeStepIndex,
  canSubmitReview,
  DEAL_STEPS,
  resolveDealStage,
  shouldShowReviewCta,
  type DealStage,
} from '../utils/dealProgress';

const stageMessage = (stage: DealStage, role: ChatRole): string => {
  switch (stage) {
    case 'chatting':
      return 'Обсудите условия занятий в переписке.';
    case 'await_student_decision':
      return role === 'student'
        ? 'Если условия вас устраивают, подтвердите отклик репетитора.'
        : 'Ученик рассматривает ваш отклик. После согласования можно будет договориться о занятиях.';
    case 'await_close':
      return role === 'student'
        ? 'Отклик принят. Закройте заявку - затем обменяйтесь контактами. Чат останется открытым до обмена.'
        : 'Ученик принял отклик. После закрытия заявки вы сможете обменяться контактами. Чат не закроется до обмена.';
    case 'ready_contacts':
      return 'Заявка закрыта. Подтвердите обмен контактами - чат останется открытым, пока оба не поделятся данными.';
    case 'contacts_pending':
      return 'Вы подтвердили обмен контактами. Ожидаем согласие собеседника.';
    case 'contacts_shared':
      return 'Контакты переданы. Можно договориться о времени и формате занятий.';
    case 'await_review':
      return role === 'student'
        ? 'Контакты переданы. Оставьте отзыв о репетиторе - можно сделать это сразу или дождаться напоминания.'
        : 'Контакты переданы. Ученик может оставить отзыв в течение окна оценки.';
    case 'rejected':
      return 'Отклик отклонен. Переписка по этой заявке завершена.';
    default:
      return '';
  }
};

interface ChatHeaderProps {
  otherUser: User;
  otherUserOnline: boolean;
  role: ChatRole;
  onProfileClick: () => void;
  bulletinId?: number | null;
  showBackButton?: boolean;
  onBack?: () => void;
  bulletin: RelatedBulletin | null;
  contactShare: ContactShareState | null;
  onAcceptResponse?: () => Promise<void>;
  onRejectResponse?: () => Promise<void>;
  onCloseBulletin?: () => Promise<void>;
  onLeaveReview?: () => void;
  disabled?: boolean;
  chatId?: number | null;
  reportOpen?: boolean;
  onReportClose?: () => void;
  hideReportButton?: boolean;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  otherUser,
  otherUserOnline,
  role,
  onProfileClick,
  bulletinId = null,
  showBackButton = false,
  onBack,
  bulletin,
  contactShare,
  onAcceptResponse,
  onRejectResponse,
  onCloseBulletin,
  onLeaveReview,
  disabled = false,
  chatId = null,
  reportOpen: reportOpenProp,
  onReportClose,
  hideReportButton = false,
}) => {
  const [loading, setLoading] = useState<'accept' | 'reject' | 'close' | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  const headerClass =
    role === 'tutor' ? classes['chat-header--tutor'] : classes['chat-header--student'];

  const displayName = formatDisplayName(otherUser, otherUser.id_user);
  const roleLine = role === 'student' ? 'Репетитор' : 'Ученик';
  const subjectLine = bulletin
    ? formatBulletinSubjectTitle({
        category: '',
        subcategory: '',
        id_category: null,
        id_subcategory: null,
        category_ru: bulletin.category_ru || '',
        subcategory_ru: bulletin.subcategory_ru || bulletin.title || '',
      })
    : '';
  const subtitle = subjectLine ? `${roleLine} / ${subjectLine}` : roleLine;
  // prod1: присутствие из websocket-hub; время выхода - из события presence или из API (last_seen_at).
  const { getLastSeen, isReady: presenceReady } = useGlobalPresence();
  const [, setPresenceTick] = useState(0);
  useEffect(() => {
    if (otherUserOnline) return undefined;
    const timer = window.setInterval(() => setPresenceTick((n) => n + 1), 60000);
    return () => window.clearInterval(timer);
  }, [otherUserOnline]);
  // r5: до снимка онлайна от websocket-hub статус неизвестен - строку не заполняем,
  // чтобы не мигало «не в сети» / «был(а) в сети» у собеседника, который на самом деле онлайн.
  const presencePending = !otherUserOnline && !presenceReady;
  const presenceText = otherUserOnline
    ? 'в сети'
    : presencePending
      ? '\u00a0'
      : formatLastSeen(getLastSeen(otherUser.id_user) ?? otherUser.last_seen_at) ?? 'не в сети';

  const run = async (action: 'accept' | 'reject' | 'close', fn?: () => Promise<void>) => {
    if (!fn || disabled || loading) return;
    setLoading(action);
    try {
      await fn();
    } finally {
      setLoading(null);
    }
  };

  const renderBulletinHeader = () => {
    if (!bulletin) return null;

    const title = formatBulletinSubjectTitle({
      category: '',
      subcategory: '',
      id_category: null,
      id_subcategory: null,
      category_ru: bulletin.category_ru || '',
      subcategory_ru: bulletin.subcategory_ru || bulletin.title || '',
    });

    return (
      <div className={classes['bulletin-header']}>
        <span className={classes['bulletin-header__label']}>Заявка</span>
        <strong className={classes['bulletin-header__title']}>{title}</strong>
      </div>
    );
  };

  const renderDealProgressContent = () => {
    if (!bulletin) return null;

    const stage = resolveDealStage(bulletin, contactShare);
    if (stage === 'none') return null;

    const step = activeStepIndex(stage);

    return (
      <div className={classes['deal-progress-content']}>
        <ol className={classes['deal-progress__steps']}>
          {DEAL_STEPS.map((item, index) => {
            const stepNumber = index + 1;
            const isDone = step > stepNumber;
            const isActive = step === stepNumber;
            return (
              <li
                key={item.key}
                className={[
                  classes['deal-progress__step'],
                  isDone ? classes['deal-progress__step--done'] : '',
                  isActive ? classes['deal-progress__step--active'] : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className={classes['deal-progress__step-dot']} aria-hidden />
                <span>{item.label}</span>
              </li>
            );
          })}
        </ol>

        <CompactText className={classes['deal-progress__message']} lines={1}>
          {stageMessage(stage, role)}
        </CompactText>

        {stage === 'await_student_decision' && role === 'student' ? (
          <div className={classes['deal-progress__actions']}>
            <button
              type='button'
              className={classes['deal-progress__btn--primary']}
              disabled={disabled || loading !== null}
              onClick={() => void run('accept', onAcceptResponse)}
            >
              {loading === 'accept' ? 'Сохранение...' : 'Принять отклик'}
            </button>
            <button
              type='button'
              className={classes['deal-progress__btn--ghost']}
              disabled={disabled || loading !== null}
              onClick={() => void run('reject', onRejectResponse)}
            >
              {loading === 'reject' ? 'Сохранение...' : 'Отклонить'}
            </button>
          </div>
        ) : null}

        {stage === 'await_close' && role === 'student' ? (
          <div className={classes['deal-progress__actions']}>
            <button
              type='button'
              className={classes['deal-progress__btn--primary']}
              disabled={disabled || loading !== null}
              onClick={() => void run('close', onCloseBulletin)}
            >
              {loading === 'close' ? 'Закрытие...' : 'Закрыть заявку и перейти к контактам'}
            </button>
          </div>
        ) : null}

        {shouldShowReviewCta(role, contactShare, stage) ? (
          <div className={classes['deal-progress__actions']}>
            <button
              type='button'
              className={`${classes['deal-progress__btn--primary']} ${classes['deal-progress__btn--review']}`}
              disabled={disabled || !onLeaveReview || !canSubmitReview(contactShare)}
              onClick={onLeaveReview}
              data-testid='chat-leave-review-cta'
              title={
                contactShare?.has_left_review
                  ? 'Вы уже оставили отзыв по этому репетитору'
                  : undefined
              }
            >
              {contactShare?.has_left_review ? 'Отзыв уже оставлен' : 'Оставить отзыв'}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={`${classes['chat-header']} ${headerClass} ${screen.headerShell}`}>
      <div
        className={`${screen.header} ${role === 'tutor' ? screen.headerTutor : screen.headerStudent} ${showBackButton && onBack ? screen.headerHasBack : ''}`}
      >
      {showBackButton && onBack ? (
        <button
          type='button'
          className={classes['chat-back-button']}
          onClick={onBack}
          aria-label='Назад'
        >
          <ArrowLeft size={18} aria-hidden />
        </button>
      ) : null}

      {/* Первый блок: Аватар + (Имя + Статус + Заявка) в колонке */}
      <div className={classes['chat-header__user-block']}>
        <ChatAvatar
          src={otherUser.avatar}
          alt={displayName}
          className={`${classes['chat-header-avatar']} ${screen.rowAvatar}`}
        />

        {/* Общая колонка: имя, статус и заявка */}
        <div className={classes['teacher-info-column']}>
          <div className={classes['teacher-details']}>
            <h3
              className={`${classes['user-fullname']} ${screen.headerName}`}
              onClick={onProfileClick}
              title={displayName}
              aria-label={`${displayName}. ${role === 'student' ? 'Открыть профиль репетитора' : 'Подробно о заявке'}`}
              style={{ cursor: 'pointer' }}
            >
              {displayName}
            </h3>

            {subtitle ? <span className={screen.headerSub}>{subtitle}</span> : null}
            <span
              className={`${screen.headerPresence} ${otherUserOnline ? screen.headerPresenceOn : ''}`}
              style={presencePending ? { visibility: 'hidden' } : undefined}
              aria-live='polite'
            >
              <span className={screen.headerPresenceDot} aria-hidden='true' />
              {presenceText}
            </span>
          </div>

          {renderBulletinHeader()}
        </div>

        {(Boolean(chatId) ||
          (role === 'tutor' && Boolean(bulletinId)) ||
          (role === 'student' && Boolean(bulletinId) && Boolean(otherUser.id_user))) && (
          <div className={classes['chat-header__nav-actions']}>
            {role === 'tutor' && bulletinId ? (
              <Link
                to={`/teacher/search/${bulletinId}`}
                className={classes['chat-header__nav-btn']}
              >
                Подробно о заявке
              </Link>
            ) : null}
            {role === 'student' && bulletinId && otherUser.id_user ? (
              <Link
                to={`/profile/details/${bulletinId}/rep/${otherUser.id_user}`}
                className={classes['chat-header__nav-btn']}
              >
                Перейти в профиль
              </Link>
            ) : null}
            {chatId && !hideReportButton ? (
              <button
                type='button'
                className={classes['chat-header__report-btn']}
                onClick={() => setReportOpen(true)}
              >
                Пожаловаться
              </button>
            ) : null}
          </div>
        )}
      </div>
      </div>

      {chatId ? (
        <ChatReportModal
          open={reportOpenProp ?? reportOpen}
          onClose={() => {
            setReportOpen(false);
            onReportClose?.();
          }}
          chatId={chatId}
          peerLabel={displayName}
        />
      ) : null}

      {/* Второй блок: Шаги + Сообщение + Кнопки */}
      {renderDealProgressContent()}
    </div>
  );
};